<template>
    <div class="inline-flex">
        <modal id="modalToolContextMenu" @close="closeModal" ref="modalRef" v-if="showModal">
            <template #header>
                Tool Settings
            </template>
            <template #body>
                <div class="toolcontextmenu-wrapper">
                    <div v-if="tool.id === 'LongPosition' || tool.id === 'ShortPosition' ">
                        <long-short-position
                            :symbol="symbol"
                            :id="tool.id"
                            @clone="handleClone"
                            v-model="form"
                            :initial-points="tool.points"
                            :initial-traits="tool.traits"
                            >
                        </long-short-position>
                    </div>
                    <div v-else class="flex flex-wrap pb-3">
                        <div v-if="showPrices">
                            <div class="w-full p-0 mt-2" v-for="(point, pointIndex) in form.points">
                                <label :for="'price_' + pointIndex">
                                    Price {{ pointIndex + 1 }}:
                                    <price-input
                                        :symbol="symbol"
                                        :base-code="baseCode"
                                        class="form-control-no-shadow form-input-sm-inline ml-1"
                                        :default-value="form.points[pointIndex].price"
                                        :id="'price_' + pointIndex"
                                        :input-width="'100px'"
                                        :disabled="disabledPriceInputs"
                                        ref="priceInputRef"
                                        v-model="form.points[pointIndex].price">
                                    </price-input>
                                </label>
                            </div>
                        </div>

                        <div class="w-full my-2" v-if="tool.id === 'FiftyPercentLine'">
                            <div class="flex">
                                <label for="52w">
                                    <input @change="toggle52w" :value="true" v-model="form.use52w" id="52w" type="checkbox" class="mr-1" />
                                    Use 52-Week High/Low Prices
                                </label>
                            </div>
                        </div>

                        <div v-for="trait in getOrderedTraits(tool.traits)" :key="trait" class="w-full p-0 mt-1">
                            <component
                                :ref="setRef(trait)"
                                v-model="form[trait]"
                                @toggle-value="togglePercentDifference"
                                :default-value="tool.traits[trait]"
                                :is="normalizeTrait(trait)"
                                :disabled="disablePercentDifference"
                            />
                        </div>

                        <div v-if="tool.id === 'Text'">
                            Press F2 while the text is selected to edit the text.
                        </div>

                        <div class="w-full p-0 mt-3">
                            <div class="flex">
                                <div @click="handleClone" class="btn btn-sm inline-flex mr-1">Clone Tool</div>
                                <div @click="handleReset" class="btn btn-sm inline-flex ml-1">Reset</div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-red inline-flex mr-1 float-left" @click="deleteTool">Delete</div>
                    <div class="btn btn-sm btn-yellow-ghost inline-flex ml-auto mr-1" @click="closeModal">Cancel</div>
                    <div class="btn btn-sm btn-yellow inline-flex ml-1" @click="handleApply">Apply</div>
                </div>
            </template>
        </modal>
        <div v-if="modalTool">
            <component
                @complete="handleApplyModal"
                @delete="deleteTool"
                @close="modalTool = false"
                :is-context="true"
                :default-traits="tool.traits"
                :default-values="tool"
                :symbol="symbol"
                :is="componentToRender"
            >
            </component>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, onBeforeMount, computed } from 'vue';

import ShowPrices from './ContextMenu/ShowPrices';
import ShowBarCount from './ContextMenu/ShowBarCount';
import ShowPercentDiff from './ContextMenu/ShowPercentDiff';
import ExtendPoint1 from './ContextMenu/ExtendPoint1';
import ExtendPoint2 from './ContextMenu/ExtendPoint2';
import LineComponent from './ContextMenu/Line';
import Line2 from './ContextMenu/Line';
import Fill from './ContextMenu/Fill';
import Fill2 from './ContextMenu/Fill';
import Factors from './ContextMenu/Factors';
import ShowTrendline from './ContextMenu/ShowTrendline';
import ShowTimestamps from './ContextMenu/ShowTimestamps';
import LongShortPosition from './ContextMenu/LongShortPosition';
import PriceInput from '../PriceInput';

import ProfitLoss from './ProfitLoss';
import RewardRisk from './RewardRisk';

const props = defineProps({
    symbol: String
});

const $clone = (obj) => JSON.parse(JSON.stringify(obj));

const emit = defineEmits(['update']);

const baseCode = ref(0);
const showModal = ref(false);
const showPrices = ref(false);
const showArrows = ref(false);

// Collection of all available tools from chart API
const tools = ref([]);

// Profit/Loss and Risk/Reward have their own modal
const modalTool = ref(false);

// Slight hack. Percent difference is it's own trait, but
// it really is a sub-trait of ShowPrices
// We need to maintain whether or not to disable it here (ugh)
// Revisit this later
const disablePercentDifference = ref(true);

const tool = ref(null);

const toolDefaultTraits = ref({
    line: {
        color: '#000',
        width: 1,
        dashStyle: 'Solid'
    },
    fill: {
        color: '#000',
        opacity: 1
    },
    showPercentDiff: false,
    showPrices: false,
    showTrendline: true,
    showBarCount: false,
    extendPoint1: false,
    extendPoint2: false,
    showTimestamps: true,
    marker: {
        lineMarkerStart: false,
        lineMarkerEnd: false
    },
    factors: [
        {
            shown: false,
            value: -0.618
        },
        {
            shown: false,
            value: -0.236
        },
        {
            shown: true,
            value: 0
        },
        {
            shown: true,
            value: 0.236
        },
        {
            shown: true,
            value: 0.382
        },
        {
            shown: true,
            value: 0.5
        },
        {
            shown: true,
            value: 0.618
        },
        {
            shown: true,
            value: 0.786
        },
        {
            shown: true,
            value: 1
        },
        {
            shown: false,
            value: 1.272
        },
        {
            shown: false,
            value: 1.618
        },
        {
            shown: false,
            value: 2.618
        }
    ]
});

const form = ref({});

const disabledPriceInputs = ref(false);
const priceInputRef = ref(null);

const componentRefs = ref({});

const setRef = (trait) => {
    // If the ref for this trait doesn't exist, create it as a new ref
    if (!componentRefs.value[trait]) {
        componentRefs.value[trait] = ref(null);
    }

    // Return a function that assigns the element to the ref
    return (el) => {
        componentRefs.value[trait] = el;
    };
};

// Computes the component to render based on the value of modalTool
const componentToRender = computed(() => {
    if (modalTool.value === 'ProfitLoss') {
        return ProfitLoss;
    } else if (modalTool.value === 'RewardRisk') {
        return RewardRisk;
    }
    return null;
});

function openToolModal(eventName, context) {
    tool.value = context;

    if (context.extras && context.extras.kind) {
        modalTool.value = context.extras.kind;
        showModal.value = false;
        return;
    }

    if (context.id === 'FiftyPercentLine') {
        form.value.use52w = false;
        disabledPriceInputs.value = false
    }

    const pTool = tools.value.find(setting => setting.id === context.id);

    showPrices.value = pTool.options.includes('allowPricesChangeUI');
    showArrows.value = pTool.options.includes('marker');

    Object.keys(tool.value.traits).forEach((key) => {
        form.value[key] = tool.value.traits[key];
        if (key === 'showPrices' && tool.value.traits[key]) {
            disablePercentDifference.value = false;
        }
    });

    if (showPrices.value) {
        form.value.points = tool.value.points;
    }

    showModal.value = true;
}

function getOrderedTraits(traits) {
    const order = ['line', 'showPrices', 'showPercentDiff', 'showBarCount', 'extendPoint1', 'extendPoint2', 'showTrendline', 'factors', 'showTimestamps'];
    return Object.keys(traits).sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

function closeModal() {
    showModal.value = false;
}

function togglePercentDifference(value) {
    disablePercentDifference.value = !value;
}

function setupEvent() {
    const api = window.Barchart.RealtimeWidgets.Charts;
    api.PubSub.subscribe(api.Topics.CH_ANNNEEDSCONTEXTMENU, openToolModal);
}

function removeEvent() {
    const api = window.Barchart.RealtimeWidgets.Charts;
    api.PubSub.unsubscribe(api.Topics.CH_ANNNEEDSCONTEXTMENU, openToolModal);
}

function deleteTool() {
    closeModal();

    emit('update', {
        id: 'Annotation',
        context: {
            id: 'Delete',
            uid: tool.value.uid
        }
    });
}

function handleClone() {
    closeModal();

    emit('update', {
        id: 'Annotation',
        context: {
            id: 'Duplicate',
            uid: tool.value.uid
        }
    });
}

function handleReset() {
    Object.keys(tool.value.traits).map((key) => {
        let defaultTrait = toolDefaultTraits.value[key];

        if (typeof defaultTrait !== 'boolean') {
            defaultTrait = $clone(defaultTrait);
        }

        if (key === 'showPrices' && ['FibonacciRetracement', 'FiftyPercentLine', 'HorizontalLine', 'VerticalLine'].includes(tool.value.id)) {
            defaultTrait = true;
        }

        // TODO
        // Fix this hack
        // This is duplicated from the 'Tools' component
        if (key === 'fill' && ['Ellipse', 'Rectangle'].includes(tool.value.id)) {
            defaultTrait = Object.assign(defaultTrait, { color: 'rgba(255, 0, 0, 0.3)' });
        }

        if (key === 'line' && ['Line', 'HorizontalLine', 'VerticalLine'].includes(tool.value.id)) {
            defaultTrait = Object.assign(defaultTrait, { width: 2 });
        }

        tool.value.traits[key] = defaultTrait;
    });

    window.localStorage.removeItem(tool.value.id);
}

function handleApply() {
    const action = {
        id: 'Annotation',
        context: {
            id: 'Update',
            uid: tool.value.uid,
            traits: form.value
        }
    };

    if (form.value.points) {
        action.context.points = form.value.points;
    }

    updateLocalStorage(tool.value.id, form.value);

    emit('update', action);
    closeModal();
}

function updateLocalStorage(toolName, toolSettings) {
    window.localStorage.setItem(toolName, JSON.stringify(toolSettings));
}

// Application of Risk/Reward Profit/Loss
function handleApplyModal(toolName, traits, extras, points) {
    const action = {
        id: 'Annotation',
        context: {
            id: 'Update',
            uid: tool.value.uid,
            traits,
            extras,
            points
        }
    };

    emit('update', action);
    modalTool.value = false;
}

/**
* <line> is a reserved tag for SVG's
* Vue complains if we try to use it as a component name
*
*/
const traitComponentMap = {
    'showPrices': ShowPrices,
    'showBarCount': ShowBarCount,
    'showPercentDiff': ShowPercentDiff,
    'extendPoint1': ExtendPoint1,
    'extendPoint2': ExtendPoint2,
    'line': LineComponent,
    'line2': Line2,
    'fill': Fill,
    'fill2': Fill2,
    'factors': Factors,
    'showTrendline': ShowTrendline,
    'showTimestamps': ShowTimestamps,
    'longShortPosition': LongShortPosition,
};

function normalizeTrait(trait) {
    console.log(trait);
    return traitComponentMap[trait] || null;
}

// Only used for 50% line
function toggle52w() {
    if (!form.value.use52w) {
        disabledPriceInputs.value = false;
    } else {
        disabledPriceInputs.value = true;
    }

    const route = window.route('api.quotes');

    let symbol;

    try {
        symbol = Object.keys(JSON.parse(window.document.getElementById('barchart-www-inline-data').innerHTML))[0];
    } catch (e) {
        console.error(e);
    }

    if (!symbol) return;

    axios.post(route, { symbol, fields: 'symbol,lowPrice1y,highPrice1y', raw: true })
        .then((response) => {
            const quotes = response.data.data[0].raw;
            form.value.points[0].price = quotes.lowPrice1y;
            form.value.points[1].price = quotes.highPrice1y;
        }).catch(error => {
            console.error(error);
        });
}

onBeforeMount(() => {
    setupEvent();
    tools.value = window.Barchart.RealtimeWidgets.Charts.getTaxonomies().annotations;
});

onMounted(() => {
    setTimeout(() => {
        baseCode.value = window.Barchart.getFieldFromInlineTemplate('unitCode');
    }, 250);
});

onBeforeUnmount(() => {
    removeEvent();
})
</script>

<style lang="scss">
#modalToolContextMenu {
    .modal-container {
        width: 24rem;
        @screen xs {
            width: 100%;
        }
    }

    .toolcontextmenu-wrapper {
        @apply m-0 mt-2 mb-2 p-0 text-sm;
    }
}
</style>

